// ⚠️ `import type`, а не импорт значением: список нужен только как ТИП, а
// значение из общего пакета утащило бы модуль в чанк. Разбор в разделе 5.1
// CLAUDE.md, а замок на это стоит в `apps/web/src/answer-barrier.test.ts`.
import type { LESSON_EXERCISE_SUBJECTS, Subject } from "@edu/shared";

/**
 * Предметы выбора темы: как они называются словами и у кого из них вообще есть
 * задания.
 *
 * ⚠️ Модуль переехал сюда из `features/homework/assign/subjects.ts` 03.09.2026.
 * Причина в том, что выбор темы понадобился второму экрану: преподаватель
 * добавляет задание прямо на уроке. Оставить словарь в форме выдачи домашки
 * значило бы тащить в комнату урока саму форму, а форма это счётчик «всего
 * заданий», строки-пики и предпросмотр состава, которым в комнате места нет.
 * Здесь остались слова и развилки, общие обоим экранам; всё, что про домашку
 * (виды источника, заказ, подписи радиокнопок), осталось в форме.
 *
 * ⚠️ Реестра генераторов здесь нет и быть не может: имена тем, уровни, потолки
 * и класс программы приезжают ДАННЫМИ с сервера (`topics-api.ts`). Отсюда
 * наружу уходят только слова, склеенные из этих данных. Замок на это стоит в
 * `apps/web/src/answer-barrier.test.ts` и в `vite.config.ts`.
 */

/** Предмет, у которого есть справочник тем: значит, есть и генераторы. */
export type TopicSubject = "math" | "english";

/**
 * Предмет в выборе темы. Предметов у продукта три (раздел 1 CLAUDE.md), и
 * русский стоит в выборе наравне с остальными, хотя заданий по нему нет ни
 * одного.
 *
 * ⚠️ Спрятать его нельзя. Преподаватель русского, не нашедший своего предмета в
 * списке, решит, что ошибся экраном, и пойдёт искать; выключенная кнопка без
 * объяснения читается как поломка. Поэтому предмет выбирается, а словами
 * говорится ровно то, что есть: заданий по нему пока нет (`one-topic-text.ts`).
 */
export type PickSubject = TopicSubject | "russian";

/**
 * Порядок предметов со справочником. Математика первой, потому что она приехала
 * первой и у неё сегодня 46 генераторов против пяти английских.
 */
export const TOPIC_SUBJECTS: readonly TopicSubject[] = ["math", "english"];

/** Предметы без единого генератора. Сегодня это только русский. */
const NO_GENERATORS: readonly PickSubject[] = ["russian"];

/**
 * Порядок предметов в выборе: сначала те, у кого задания есть.
 *
 * ⚠️ Список СКЛЕЕН из двух, а не выписан третьим местом. Выпиши его руками, и
 * приезд генераторов по русскому потребовал бы правки в двух файлах, из которых
 * второй никто не вспомнит: предмет остался бы в хвосте с пометкой «заданий
 * нет» при полном справочнике.
 */
export const PICK_SUBJECTS: readonly PickSubject[] = [...TOPIC_SUBJECTS, ...NO_GENERATORS];

/**
 * Предметы выбора темы, которые ведёт преподаватель, в порядке `PICK_SUBJECTS`.
 *
 * ⚠️ Список преподавателя приезжает уже разрешённым (`useTeacherSubjects`):
 * сырое `me.subjects` с его `null` здесь дало бы пустоту у каждого, кого не
 * настраивали. Пустой ответ законен (преподаватель одного немецкого), и тогда
 * блок выбора темы не показывается вовсе.
 */
export function pickSubjectsOf(teacher: readonly Subject[]): PickSubject[] {
  return PICK_SUBJECTS.filter((s) => teacher.includes(s));
}

/** То же для предметов со справочником: форма выдачи домашки. */
export function topicSubjectsOf(teacher: readonly Subject[]): TopicSubject[] {
  return TOPIC_SUBJECTS.filter((s) => teacher.includes(s));
}

/**
 * Карты, а не `switch`: пропущенный предмет здесь это ошибка компиляции, а
 * пропущенная ветка `switch` без `noImplicitReturns` молча вернула бы
 * `undefined`.
 */
const SUBJECT_WORD: Record<PickSubject, string> = {
  math: "математика",
  english: "английский",
  russian: "русский",
};

/** «темы ПО математике»: тот же предмет в другом падеже, второй строкой. */
const SUBJECT_BY: Record<PickSubject, string> = {
  math: "по математике",
  english: "по английскому",
  russian: "по русскому",
};

/**
 * Пространства имён навыков, которыми ручка называет свои справочники
 * (`routes/homework-topics.ts`): у математики `oge-math`, у английского
 * `english`. Это НЕ имя параметра запроса, оно другое и пришло из формы.
 */
const SUBJECT_NAMESPACE: Record<TopicSubject, readonly string[]> = {
  math: ["oge-math"],
  english: ["english"],
};

/**
 * Как назвать предмет в заказе задания на урок
 * (`POST /v1/lessons/:id/exercises`).
 *
 * ⚠️ **Имён у предмета два, и путать их нельзя.** В параметре запроса
 * справочника тем он `math`, а в ответе того же справочника и в заказе задания
 * он `oge-math`: первое пришло из формы выдачи домашки, второе это
 * пространство имён навыка. Карта нужна ровно затем, чтобы это расхождение
 * жило в ОДНОМ месте, а не переводилось на глаз в каждом вызывающем.
 *
 * ⚠️ `Record` по `TopicSubject`, а не `switch` и не приведение типом: третий
 * предмет, добавленный в продукт, обязан упереться в ошибку компиляции здесь и
 * получить решение, а не проехать молча. Русский сюда не попадает и попасть не
 * может: `TopicSubject` это предметы, У КОТОРЫХ ЕСТЬ генераторы, а у русского
 * их нет ни одного.
 */
const SUBJECT_IN_ORDER: Record<TopicSubject, (typeof LESSON_EXERCISE_SUBJECTS)[number]> = {
  math: "oge-math",
  english: "english",
};

export function orderSubject(subject: TopicSubject): (typeof LESSON_EXERCISE_SUBJECTS)[number] {
  return SUBJECT_IN_ORDER[subject];
}

/** Предмет одним словом: «математика». */
export function subjectWord(subject: PickSubject): string {
  return SUBJECT_WORD[subject];
}

/** Предмет с предлогом: «по математике». */
export function subjectBy(subject: PickSubject): string {
  return SUBJECT_BY[subject];
}

/**
 * У этого предмета есть справочник тем, то есть его вообще можно спрашивать у
 * сервера.
 *
 * ⚠️ Предикат нужен ровно затем, чтобы за русским НЕ ходил запрос. Ручка
 * разбирает предмет строго и отвечает на неизвестный `invalid_query`
 * (`routes/homework-topics.ts`), то есть спросив, мы получили бы отказ и сказали
 * бы «темы не загрузились» с кнопкой «Повторить». Это неправда дважды: ничего не
 * ломалось, и повторять нечего.
 */
export function hasGenerators(subject: PickSubject): subject is TopicSubject {
  return (TOPIC_SUBJECTS as readonly PickSubject[]).includes(subject);
}

/**
 * Приехал справочник ЧУЖОГО предмета.
 *
 * ⚠️ Проверка нужна из-за перекоса версий: новый бандл против старого API в
 * окно выкатки либо откат API. Ручка до английской волны отвечала математикой
 * на любой запрос, включая `?subject=english`, и без этой проверки
 * математический справочник молча встал бы в английскую ветку. Отказ приехал бы
 * только на раздаче, по строке на каждого ученика, потому что заказ ушёл бы с
 * `kind: "eng"` и математическими слагами.
 *
 * ⚠️ Отказ требует ДОКАЗАТЕЛЬСТВА чужого предмета, а не отсутствия знакомого:
 * незнакомое имя пространства это будущий предмет или переименование, и гасить
 * из-за него живую ветку хуже, чем пропустить. Гасим только тогда, когда имя
 * принадлежит другому предмету из тех, что мы спрашиваем.
 */
export function isForeignTopics(asked: TopicSubject, subject: string): boolean {
  const name = subject.trim().toLowerCase();
  if (SUBJECT_NAMESPACE[asked].includes(name)) return false;
  return TOPIC_SUBJECTS.some((s) => s !== asked && SUBJECT_NAMESPACE[s].includes(name));
}

/**
 * Уровень материала темы словами: у математики «5 класс», у английского
 * «уровень A2» (раздел 7, пункт 15 `docs/english-generators-design.md`).
 *
 * ⚠️ Это НЕ уровень задания. Рядом стоит переключатель «лёгкий, средний,
 * сложный», и две шкалы путаются тем легче, что у английского обе называются
 * словом «уровень». Поэтому подпись начинается со слова «Материал»: оно
 * привязывает число к теме, а не к сложности. Раздел 3 проекта английского
 * разбирает, почему эти оси не коррелируют даже по знаку: `past-simple` это A1,
 * но задание уровня 2 по нему труднее задания уровня 1 по `past-perfect` (B1).
 *
 * `grade` приезжает справочником как есть («5», «5-6», «A2»), склейка тут.
 */
export function materialLine(subject: TopicSubject, grade: string): string {
  return subject === "english" ? `Материал: уровень ${grade}` : `Материал: ${grade} класс`;
}
